import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUnsaved } from "@/context/UnsavedContext";
import { api } from "@/lib/api";
import { changelogSeenKey } from "@/pages/WhatsNew";
import {
  Mic,
  Inbox,
  ShieldCheck,
  LogOut,
  MessageSquareText,
  BookOpen,
  Sparkles,
  UserCircle2,
  Trophy,
} from "lucide-react";
import { FeedbackDialog } from "@/components/FeedbackDialog";

/**
 * Global shell — top app bar + mobile bottom nav.
 * Renders nothing while logged out (Login screen owns its own chrome).
 */
export function Nav() {
  const { user, logout } = useAuth();
  const { confirmNavigateAway } = useUnsaved();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/changelog");
        if (cancelled) return;
        const key = changelogSeenKey(user.id);
        let seen = null;
        try { seen = localStorage.getItem(key); } catch { /* noop */ }
        if (seen === null) {
          // First visit ever (no baseline set yet): treat all CURRENT entries as already
          // seen so the badge starts at 0 — only entries published AFTER this count.
          const baseline = data.reduce((max, e) => {
            const s = `${e.datum}|${e.created_at || ""}`;
            return s > max ? s : max;
          }, "");
          try { localStorage.setItem(key, baseline); } catch { /* noop */ }
          setNewCount(0);
          return;
        }
        const count = data.filter((e) => `${e.datum}|${e.created_at || ""}` > seen).length;
        setNewCount(count);
      } catch { /* fail-soft: never block the nav */ }
    })();
    const onSeen = () => setNewCount(0);
    window.addEventListener("tw1:changelog-seen", onSeen);
    return () => { cancelled = true; window.removeEventListener("tw1:changelog-seen", onSeen); };
  }, [user]);

  if (!user) return null;

  const guardClick = (e) => {
    if (!confirmNavigateAway()) e.preventDefault();
  };

  const handleLogout = () => {
    if (!confirmNavigateAway()) return;
    logout();
    navigate("/login");
  };

  const isAdmin = user.role === "admin";
  const isReader = user.role === "lezer";
  const bottomCols = (isReader ? 0 : 2) + 1 + 1 + (isAdmin ? 1 : 0);
  const gridColsClass = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4", 5: "grid-cols-5" }[bottomCols] || "grid-cols-3";

  return (
    <>
      {/* ---------- Top app bar ---------- */}
      <header className="sticky top-0 z-40 bg-tw-black/95 backdrop-blur border-b border-tw">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 sm:gap-6 px-4 sm:px-8 h-14 sm:h-16">
          {/* Brand mark */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-1.5 h-8 sm:h-9 bg-tw-yellow shrink-0" aria-hidden />
            <div className="leading-tight min-w-0">
              <div className="font-display text-base sm:text-lg font-black text-white tracking-tight">
                TW1
              </div>
              <div className="font-mono text-[10px] tracking-[0.24em] text-tw-muted uppercase">
                Brain&nbsp;intake
              </div>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1" aria-label="Hoofdnavigatie">
            {!isReader && (
              <>
                <DesktopLink to="/" end onClick={guardClick} testId="nav-record" icon={Mic} label="Opnemen" />
                <DesktopLink to="/submissions" onClick={guardClick} testId="nav-submissions" icon={Inbox} label="Mijn kluis" />
              </>
            )}
            <DesktopLink to="/bibliotheek" onClick={guardClick} testId="nav-bibliotheek" icon={BookOpen} label="Bibliotheek" />
            <DesktopLink to="/wat-is-nieuw" onClick={guardClick} testId="nav-whatsnew" icon={Sparkles} label="Nieuw" badge={newCount} />
            {isAdmin && (
              <DesktopLink to="/admin" onClick={guardClick} testId="nav-admin" icon={ShieldCheck} label="Beheer" />
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <NavLink to="/weekstand" onClick={guardClick} data-testid="nav-weekstand" title="Weekstand" aria-label="Weekstand" className="tw-press p-2 text-tw-yellow hover:text-white"><Trophy size={16} /></NavLink>
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              data-testid="nav-feedback-btn"
              title="Feedback"
              className="tw-press hidden sm:flex items-center gap-2 px-3 py-2 border border-tw text-tw-muted hover:text-tw-yellow hover:border-tw-yellow uppercase tracking-[0.2em] text-[10px] font-bold focus:outline-none focus-visible:border-tw-yellow"
            >
              <MessageSquareText size={14} strokeWidth={2.5} />
              <span>Feedback</span>
            </button>
            {/* Mobile-only compact feedback icon */}
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              data-testid="nav-feedback-icon-btn"
              title="Feedback"
              aria-label="Feedback"
              className="tw-press sm:hidden p-2 text-tw-muted hover:text-tw-yellow"
            >
              <MessageSquareText size={16} strokeWidth={2.5} />
            </button>

            {/* Mobile-only account icon (desktop reaches /account via the name/role block) */}
            <button
              type="button"
              onClick={() => { if (confirmNavigateAway()) navigate("/account"); }}
              data-testid="nav-account-icon-btn"
              title="Mijn account"
              aria-label="Mijn account"
              className="tw-press xl:hidden p-2 text-tw-muted hover:text-tw-yellow"
            >
              <UserCircle2 size={16} strokeWidth={2.5} />
            </button>

            <button
              type="button"
              onClick={() => { if (confirmNavigateAway()) navigate("/account"); }}
              data-testid="nav-user-account-btn"
              title="Mijn account"
              className="tw-press hidden xl:block text-right leading-tight hover:opacity-80 focus:outline-none max-w-[120px] break-words"
            >
              <div className="text-xs text-white font-medium" data-testid="nav-user-name">{user.name}</div>
              <div className="font-mono text-[10px] text-tw-muted uppercase">{{ user: "Gebruiker", admin: "Beheerder", lezer: "Lezer" }[user.role] || user.role}</div>
            </button>
            <button
              onClick={handleLogout}
              data-testid="nav-logout-btn"
              aria-label="Uitloggen"
              title="Uitloggen"
              className="tw-press p-2 text-tw-muted hover:text-tw-red"
            >
              <LogOut size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </header>

      {/* ---------- Mobile bottom nav ---------- */}
      <nav
        data-testid="bottom-nav"
        aria-label="Onderaan navigatie"
        className="xl:hidden fixed bottom-0 inset-x-0 z-40 bg-tw-black/95 backdrop-blur border-t border-tw"
      >
        <div className={`grid ${gridColsClass} max-w-lg mx-auto`}>
          {!isReader && (
            <>
              <BottomLink to="/" end onClick={guardClick} testId="nav-bottom-record" icon={Mic} label="Opnemen" />
              <BottomLink to="/submissions" onClick={guardClick} testId="nav-bottom-submissions" icon={Inbox} label="Mijn kluis" />
            </>
          )}
          <BottomLink to="/bibliotheek" onClick={guardClick} testId="nav-bottom-bibliotheek" icon={BookOpen} label="Bibliotheek" />
          <BottomLink to="/wat-is-nieuw" onClick={guardClick} testId="nav-bottom-whatsnew" icon={Sparkles} label="Nieuw" badge={newCount} />
          {isAdmin && (
            <BottomLink to="/admin" onClick={guardClick} testId="nav-bottom-admin" icon={ShieldCheck} label="Beheer" />
          )}
        </div>
        {/* iOS home-indicator safe-area padding */}
        <div className="h-[env(safe-area-inset-bottom)]" aria-hidden />
      </nav>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}

function DesktopLink({ to, end, onClick, testId, icon: Icon, label, badge = 0 }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        `relative flex items-center gap-2 px-3.5 py-2.5 text-xs uppercase tracking-[0.2em] font-medium border-b-2 transition-colors duration-150 ${
          isActive
            ? "text-tw-yellow border-tw-yellow"
            : "text-tw-muted border-transparent hover:text-white"
        }`
      }
    >
      <span className="relative">
        <Icon size={14} strokeWidth={2.5} />
        {badge > 0 && (
          <span data-testid={`${testId}-badge`} className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-[3px] rounded-full bg-tw-yellow text-black font-mono text-[8px] font-bold flex items-center justify-center leading-none" aria-hidden>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      <span>{label}</span>
    </NavLink>
  );
}

function BottomLink({ to, end, onClick, testId, icon: Icon, label, badge = 0 }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        `tw-press relative flex flex-col items-center justify-center gap-1 px-0.5 py-3 text-[9px] uppercase tracking-[0.1em] font-medium transition-colors duration-150 ${
          isActive ? "text-tw-yellow" : "text-tw-muted hover:text-white"
        }`
      }
    >
      <span className="relative">
        <Icon size={20} strokeWidth={2.5} />
        {badge > 0 && (
          <span data-testid={`${testId}-badge`} className="absolute -top-1 -right-2 min-w-[15px] h-[15px] px-[3px] rounded-full bg-tw-yellow text-black font-mono text-[8px] font-bold flex items-center justify-center leading-none" aria-hidden>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      <span className="max-w-full truncate leading-none">{label}</span>
    </NavLink>
  );
}
